//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Accordion, Container, Row, Col, ListGroup, Card, OverlayTrigger, Tooltip } from 'react-bootstrap'; 
import HistorySortControls from './HistorySortControls';

// Context
import { useLanguage } from '../../../../../API/Context/LanguageContext';

// Utils
import { transformedDate } from '../../../../../Utils/dateUtils';

// Config
import { VocabSummary as smry_vocab } from '../../../../../ConfigFiles/multilingual/VocabSummary';
import { VocabDiagnosis } from '../../../../../ConfigFiles/multilingual/VocabDiagnosis';
//  ----------      END   IMPORT  BLOCK       ----------

const ClinicHistoryDisplay = ({
  diagnoses = [],
  prescriptions = [],
  requested_id = null,
}) => {
  const { lan } = useLanguage();

  const [sortByDate, setSortByDate] = useState('desc');
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Map prescriptions by diagnosis_id
  const prescriptionsMap = useMemo(() => {
    const map = new Map();
    const prescriptionsArray = Array.isArray(prescriptions) ? prescriptions : [];
    prescriptionsArray.forEach((p) => {
      if (p.diagnosis_id && p.raw_text) {
        map.set(p.diagnosis_id, p.raw_text);
      }
    });
    return map;
  }, [prescriptions]);

  // Sort diagnoses based on current order flag
  const sortedDiagnoses = useMemo(() => {
    const listCopy = [...diagnoses];
    listCopy.sort((a, b) => {
      const dateA = new Date(a.creation_date);
      const dateB = new Date(b.creation_date);
      if (dateA < dateB) return sortByDate === 'asc' ? -1 : 1;
      if (dateA > dateB) return sortByDate === 'asc' ? 1 : -1;
      return 0;
    });
    return listCopy;
  }, [diagnoses, sortByDate]);

  const getMatchedObject = useCallback((my_id) => prescriptionsMap.get(my_id) || '', [prescriptionsMap]);

  // Respond when active patient changes or new requested_id arrives
  useEffect(() => {
    if (requested_id) {
      const foundEntry = diagnoses.find((entry) => entry._id === requested_id);
      if (foundEntry) {
        setSelectedEntry(foundEntry);
        return;
      }
    }
    // Fallback: Default to the first available record
    if (sortedDiagnoses.length > 0) {
      setSelectedEntry(sortedDiagnoses[0]);
    } else {
      setSelectedEntry(null);
    }
  }, [requested_id, diagnoses, sortedDiagnoses]);

  const handleSelectEntry = (entry) => {
    setSelectedEntry(entry);
  };

  if (!selectedEntry) {
    return <p className="text-muted fst-italic py-2 m-0">{smry_vocab.loading_data[lan]}</p>;
  }

  const areVitalsComplete =
    selectedEntry.vitals && Object.values(selectedEntry.vitals).every((v) => v !== undefined && v !== null);

  return (
    <Container className="p-0">
      <Row>
        {/* Left Column: Entry List */}
        <Col md={4}>
          <div className="d-flex justify-content-between mb-2">
            <HistorySortControls sortByDate={sortByDate} setSortByDate={setSortByDate} />
          </div>

          <ListGroup style={{ maxHeight: '350px', overflowY: 'auto' }}>
            {sortedDiagnoses.map((entry) => (
              <ListGroup.Item
                key={entry._id}
                action
                onClick={() => handleSelectEntry(entry)}
                active={selectedEntry._id === entry._id}
                className="d-flex justify-content-between align-items-center"
              >
                <span>{transformedDate(entry.creation_date)}</span>
                <span className="d-flex gap-2">
                  {prescriptionsMap.has(entry._id) && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`pres-tip-${entry._id}`}>{VocabDiagnosis.prescription[lan]}</Tooltip>}
                    >
                      <span className="text-success" style={{ fontSize: '1rem' }}>
                        📎
                      </span>
                    </OverlayTrigger>
                  )}
                  {entry.laboratory_tests && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`lab-tip-${entry._id}`}>{VocabDiagnosis.laboratory_tests[lan]}</Tooltip>}
                    >
                      <span className="text-info" style={{ fontSize: '1rem' }}>
                        🧪
                      </span>
                    </OverlayTrigger>
                  )}
                </span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Col>

        {/* Right Column: Active Record Detail */}
        <Col md={8}>
          <Card>
            <Card.Header>{transformedDate(selectedEntry.creation_date)}</Card.Header>

            {selectedEntry.vitals && (
              <Accordion flush>
                <Accordion.Item eventKey="vitals">
                  <Accordion.Header>{VocabDiagnosis.vitals[lan]}</Accordion.Header>
                  <Accordion.Body>
                    <ListGroup>
                      {areVitalsComplete ? (
                        <>
                          <ListGroup.Item>{VocabDiagnosis.temperature[lan]}: {selectedEntry.vitals.temperature}</ListGroup.Item>
                          <ListGroup.Item>{VocabDiagnosis.heart_rate[lan]}: {selectedEntry.vitals.heart_rate}</ListGroup.Item>
                          <ListGroup.Item>{VocabDiagnosis.blood_pressure_systolic[lan]}: {selectedEntry.vitals.blood_pressure_systolic}</ListGroup.Item>
                          <ListGroup.Item>{VocabDiagnosis.blood_pressure_diastolic[lan]}: {selectedEntry.vitals.blood_pressure_diastolic}</ListGroup.Item>
                          <ListGroup.Item>{VocabDiagnosis.oxygen[lan]}: {selectedEntry.vitals.oxygen}</ListGroup.Item>
                        </>
                      ) : (
                        <ListGroup.Item>{VocabDiagnosis.no_vitals_recorded?.[lan] || 'No vitals recorded.'}</ListGroup.Item>
                      )}
                    </ListGroup>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}

            <Card.Body>
              {selectedEntry.interrogatory && (
                <>
                  <Card.Subtitle className="mb-2 text-muted">{VocabDiagnosis.interrogation[lan]}</Card.Subtitle>
                  <p>{selectedEntry.interrogatory}</p>
                  <hr className="my-2" />
                </>
              )}

              {selectedEntry.exploration && (
                <>
                  <Card.Subtitle className="mb-2 text-muted">{VocabDiagnosis.exploration[lan]}</Card.Subtitle>
                  <p>{selectedEntry.exploration}</p>
                  <hr className="my-2" />
                </>
              )}

              <Card.Subtitle className="mb-2 text-muted">{VocabDiagnosis.diagnosis[lan]}</Card.Subtitle>
              <Card.Text>{selectedEntry.diagnosis}</Card.Text>
              <hr className="my-2" />

              {getMatchedObject(selectedEntry._id) && (
                <>
                  <Card.Subtitle className="mb-2 text-muted">{VocabDiagnosis.prescription[lan]}</Card.Subtitle>
                  <Card.Text>{getMatchedObject(selectedEntry._id)}</Card.Text>
                  <hr className="my-2" />
                </>
              )}

              {selectedEntry.laboratory_tests && (
                <>
                  <Card.Subtitle className="mb-2 text-muted">{VocabDiagnosis.laboratory_tests[lan]}</Card.Subtitle>
                  <p>{selectedEntry.laboratory_tests}</p>
                  <hr className="my-2" />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ClinicHistoryDisplay;